import { useState, useEffect, useCallback } from 'react';
import { refreshRoundWeather } from '../services/roundService';
import { RateLimitError } from '../services/weatherService';

// UX throttle for the manual "Fetch / Refresh weather" CTA on a round.
// Two layers, both client-side; the real hard cap is the per-user weather
// budget enforced server-side inside the refresh-round-weather edge function.
const COOLDOWN_MS = 60 * 1000; // disable the button for 60s after any call
const FRESHNESS_MS = 10 * 60 * 1000; // "Refresh" only offered once data is >10 min old

// Hook wrapping refreshRoundWeather with a cooldown + freshness gate.
// - `Fetch` (no weather yet): enabled unless in cooldown / in flight.
// - `Refresh` (weather present): additionally gated on `isStale`.
export function useWeatherRefresh({ roundId, weatherUpdatedAt, onRefreshed }) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [tick, setTick] = useState(Date.now());
  const [error, setError] = useState(null);

  // Re-render once a second while cooling down so the countdown updates.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const cooldownRemainingMs = Math.max(0, cooldownUntil - tick);
  const inCooldown = cooldownRemainingMs > 0;
  const cooldownRemainingSec = Math.ceil(cooldownRemainingMs / 1000);

  const updatedMs = weatherUpdatedAt ? new Date(weatherUpdatedAt).getTime() : 0;
  const isStale = !updatedMs || Date.now() - updatedMs >= FRESHNESS_MS;

  const refresh = useCallback(async () => {
    if (isRefreshing || inCooldown || !roundId) return null;
    setIsRefreshing(true);
    setError(null);
    try {
      const result = await refreshRoundWeather(roundId);
      setCooldownUntil(Date.now() + COOLDOWN_MS);
      if (onRefreshed) await onRefreshed(result);
      return result;
    } catch (err) {
      // Cool down even on failure so a broken state can't be hammered.
      setCooldownUntil(Date.now() + COOLDOWN_MS);
      if (err instanceof RateLimitError) {
        setError({ type: 'rate-limit', message: err.message, resetAt: err.resetAt });
      } else {
        setError({ type: 'error', message: err.message || 'Could not refresh weather right now.' });
      }
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [roundId, isRefreshing, inCooldown, onRefreshed]);

  return {
    refresh,
    isRefreshing,
    inCooldown,
    cooldownRemainingSec,
    isStale,
    error,
  };
}
